import type { AxiosInstance } from 'axios';

export type BusinessAccountStatus = 'PENDING_APPROVAL' | 'ACTIVE' | 'SUSPENDED' | 'BLOCKED';
export type BusinessAccountUserRole = 'OWNER' | 'BUYER' | 'FINANCE' | 'VIEWER';
export type CreditTerms = 'PREPAID' | 'NET_15' | 'NET_30' | 'NET_60' | 'NET_90';
export type RfqStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'QUOTED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'CONVERTED'
  | 'EXPIRED';
export type B2bOrderStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'CONFIRMED' | 'FULFILLED' | 'CANCELLED';
export type B2bInvoiceStatus = 'PENDING' | 'PARTIALLY_PAID' | 'PAID' | 'OVERDUE' | 'CANCELLED';
export type B2bPaymentMethod =
  | 'CASH'
  | 'UPI'
  | 'BANK_TRANSFER'
  | 'CHEQUE'
  | 'RAZORPAY'
  | 'CREDIT_NOTE';

export type B2bContractStatus = 'DRAFT' | 'PENDING_APPROVAL' | 'ACTIVE' | 'REJECTED' | 'EXPIRED';

export interface BusinessAccount {
  id: string;
  code: string;
  name: string;
  legalName: string | null;
  gstin: string | null;
  panNumber: string | null;
  email: string | null;
  phone: string | null;
  stateCode: string;
  creditLimitPaise: number;
  outstandingPaise: number;
  defaultCreditTerms: CreditTerms;
  status: BusinessAccountStatus;
  gstCertificateUrl?: string | null;
  panCardUrl?: string | null;
  creditApplicationUrl?: string | null;
  creditApprovedById?: string | null;
  creditApprovedAt?: string | null;
  salesRepId: string | null;
  approvedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface B2bContract {
  id: string;
  businessAccountId: string;
  variantId: string;
  unitPricePaise: number;
  minOrderQty: number;
  validFrom: string;
  validTo: string | null;
  status: B2bContractStatus;
  approvedById: string | null;
  approvedAt: string | null;
  rejectedById: string | null;
  rejectedAt: string | null;
  rejectedReason: string | null;
  notes: string | null;
  createdAt: string;
}

export interface BusinessAccountUser {
  id: string;
  businessAccountId: string;
  userId: string;
  role: BusinessAccountUserRole;
  createdAt: string;
}

export interface SalesRep {
  id: string;
  userId: string;
  code: string;
  commissionBps: number;
  active: boolean;
  createdAt: string;
}

export interface RfqItem {
  id: string;
  variantId: string;
  description: string;
  qty: number;
  requestedUnitPricePaise: number | null;
  quotedUnitPricePaise: number | null;
  gstRate: number;
}

export interface Rfq {
  id: string;
  rfqNumber: string;
  businessAccountId: string;
  requestedById: string;
  status: RfqStatus;
  quotedById: string | null;
  expectedDeliveryAt: string | null;
  validUntil: string | null;
  notes: string | null;
  quotedAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  convertedOrderId: string | null;
  createdAt: string;
}

export interface RfqDetail extends Rfq {
  items: RfqItem[];
}

export interface B2bOrderItem {
  id: string;
  variantId: string;
  description: string;
  sku: string | null;
  hsnSac: string | null;
  qty: number;
  unitPricePaise: number;
  gstRate: number;
  taxPaise: number;
  discountPaise: number;
  lineSubtotalPaise: number;
  cogsPaise: number;
}

export interface B2bOrder {
  id: string;
  orderNumber: string;
  businessAccountId: string;
  rfqId: string | null;
  createdById: string;
  status: B2bOrderStatus;
  warehouseId: string | null;
  itemsSubtotalPaise: number;
  taxPaise: number;
  shippingPaise: number;
  discountPaise: number;
  grandTotalPaise: number;
  cogsPaise: number;
  intraState: boolean;
  creditTerms: CreditTerms;
  approvedAt: string | null;
  fulfilledAt: string | null;
  cancelledAt: string | null;
  notes: string | null;
  createdAt: string;
}

export interface B2bOrderDetail extends B2bOrder {
  items: B2bOrderItem[];
}

export interface B2bInvoice {
  id: string;
  invoiceNumber: string;
  b2bOrderId: string;
  businessAccountId: string;
  status: B2bInvoiceStatus;
  subtotalPaise: number;
  taxPaise: number;
  totalPaise: number;
  paidPaise: number;
  dueAt: string;
  issuedAt: string;
  paidAt: string | null;
  cancelledAt: string | null;
  notes: string | null;
}

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  stateCode: string;
  pincode: string;
}

export interface CreateBusinessAccountRequest {
  code: string;
  name: string;
  legalName?: string;
  gstin?: string;
  panNumber?: string;
  email?: string;
  phone?: string;
  billingAddress: Address;
  shippingAddress?: Address;
  stateCode: string;
  creditLimitPaise?: number;
  defaultCreditTerms?: CreditTerms;
  gstCertificateUrl?: string;
  panCardUrl?: string;
  creditApplicationUrl?: string;
  notes?: string;
}

/** One rung of a variant's bulk-price ladder. */
export interface B2bPriceTier {
  id: string;
  variantId: string;
  minQty: number;
  pricePaise: number;
}

export function b2bEndpoints(http: AxiosInstance) {
  return {
    /**
     * Public: the bulk-price ladder for a variant, for a product page to show.
     * Empty for a variant that has no tiers, which is most of them.
     */
    priceTiers: (variantId: string) =>
      http
        .get<{ data: B2bPriceTier[] }>(`/b2b/variants/${variantId}/price-tiers`)
        .then((r) => r.data.data),

    // Accounts
    listAccounts: () =>
      http.get<{ data: BusinessAccount[] }>('/admin/b2b/accounts').then((r) => r.data.data),
    createAccount: (body: CreateBusinessAccountRequest) =>
      http.post<BusinessAccount>('/admin/b2b/accounts', body).then((r) => r.data),
    getAccount: (accountId: string) =>
      http.get<BusinessAccount>(`/b2b/accounts/${accountId}`).then((r) => r.data),
    updateAccount: (
      accountId: string,
      body: Partial<CreateBusinessAccountRequest> & { status?: BusinessAccountStatus },
    ) => http.patch<BusinessAccount>(`/admin/b2b/accounts/${accountId}`, body).then((r) => r.data),
    reviewAccount: (
      accountId: string,
      body: { creditLimitPaise: number; creditTerms: CreditTerms; status?: BusinessAccountStatus },
    ) =>
      http
        .post<BusinessAccount>(`/admin/b2b/accounts/${accountId}/review`, body)
        .then((r) => r.data),
    listUsers: (accountId: string) =>
      http
        .get<{ data: BusinessAccountUser[] }>(`/admin/b2b/accounts/${accountId}/users`)
        .then((r) => r.data.data),
    addUser: (accountId: string, body: { userId: string; role?: BusinessAccountUserRole }) =>
      http.post(`/admin/b2b/accounts/${accountId}/users`, body).then((r) => r.data),
    removeUser: (membershipId: string) =>
      http.delete(`/admin/b2b/account-users/${membershipId}`).then((r) => r.data),

    // Contracts
    listContracts: () =>
      http.get<{ data: B2bContract[] }>('/admin/b2b/contracts').then((r) => r.data.data),
    createContract: (body: {
      businessAccountId: string;
      variantId: string;
      unitPricePaise: number;
      minOrderQty?: number;
      validFrom?: string;
      validTo?: string;
      notes?: string;
    }) => http.post<B2bContract>('/admin/b2b/contracts', body).then((r) => r.data),
    updateContractStatus: (
      contractId: string,
      body: { status: B2bContractStatus; rejectedReason?: string },
    ) =>
      http
        .patch<B2bContract>(`/admin/b2b/contracts/${contractId}/status`, body)
        .then((r) => r.data),

    // Sales reps
    listSalesReps: () =>
      http.get<{ data: SalesRep[] }>('/admin/b2b/sales-reps').then((r) => r.data.data),
    createSalesRep: (body: { userId: string; code: string; commissionBps?: number }) =>
      http.post<SalesRep>('/admin/b2b/sales-reps', body).then((r) => r.data),

    // RFQ
    listRfqs: () => http.get<{ data: Rfq[] }>('/b2b/rfqs').then((r) => r.data.data),
    getRfq: (rfqId: string) => http.get<RfqDetail>(`/b2b/rfqs/${rfqId}`).then((r) => r.data),
    quoteRfq: (
      rfqId: string,
      body: {
        lines: { rfqItemId: string; quotedUnitPricePaise: number }[];
        validUntil?: string;
        notes?: string;
      },
    ) => http.post(`/admin/b2b/rfqs/${rfqId}/quote`, body).then((r) => r.data),
    acceptRfq: (rfqId: string) => http.post(`/b2b/rfqs/${rfqId}/accept`).then((r) => r.data),
    rejectRfq: (rfqId: string) => http.post(`/b2b/rfqs/${rfqId}/reject`).then((r) => r.data),

    // Orders
    listOrders: () => http.get<{ data: B2bOrder[] }>('/b2b/orders').then((r) => r.data.data),
    getOrder: (orderId: string) =>
      http.get<B2bOrderDetail>(`/b2b/orders/${orderId}`).then((r) => r.data),
    approveOrder: (orderId: string, body: { warehouseId: string; note?: string }) =>
      http.post(`/admin/b2b/orders/${orderId}/approve`, body).then((r) => r.data),
    cancelOrder: (orderId: string) =>
      http.post(`/b2b/orders/${orderId}/cancel`).then((r) => r.data),
    fulfillOrder: (orderId: string) =>
      http.post(`/admin/b2b/orders/${orderId}/fulfill`).then((r) => r.data),

    // Invoices
    listInvoices: () => http.get<{ data: B2bInvoice[] }>('/b2b/invoices').then((r) => r.data.data),
    getInvoice: (invoiceId: string) =>
      http.get<B2bInvoice>(`/b2b/invoices/${invoiceId}`).then((r) => r.data),
    recordPayment: (
      invoiceId: string,
      body: { amountPaise: number; method: B2bPaymentMethod; reference?: string; notes?: string },
    ) => http.post(`/admin/b2b/invoices/${invoiceId}/payments`, body).then((r) => r.data),
  };
}

export type B2bEndpoints = ReturnType<typeof b2bEndpoints>;
